import { AutocompleteOption } from '@Components/Form/FormAutoComplete/FormAutoComplete';
import { useGetCountryMasterData } from '@Domains/MasterData';
import { Search } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useEffect, useState } from 'react';

type CountryDialogProps = DialogProps & {
  onCountrySubmit: (
    selectedCountry: AutocompleteOption,
    editIndex?: number
  ) => void;
  selectedCountry?: AutocompleteOption;
  existingCountries?: AutocompleteOption[];
  isEdit?: boolean;
  editIndex?: number;
};

const CountryDialog = ({
  onCountrySubmit,
  selectedCountry,
  existingCountries = [],
  onClose,
  isEdit = false,
  editIndex,
  ...restProps
}: CountryDialogProps) => {
  const [{ data, loading, error }] = useGetCountryMasterData('EXPORT_INCOME');
  const [country, setCountry] = useState<AutocompleteOption | null>(null);

  useEffect(() => {
    if (selectedCountry) setCountry(selectedCountry);
  }, [selectedCountry]);

  const getCountryOptions = () =>
    data
      ?.map((country) => ({
        label: country.nameTh,
        value: country.nameEn,
      }))
      .filter(
        (option) =>
          (isEdit && selectedCountry?.value === option.value) ||
          !existingCountries.some((existing) => existing.value === option.value)
      ) || [];

  const handleClose = () => {
    if (onClose) onClose({}, 'backdropClick');
    setCountry(null);
  };

  const handleSubmit = () => {
    if (!country) return;
    onCountrySubmit(country, editIndex);
    setCountry(null);
    handleClose();
  };

  if (loading) {
    return (
      <Dialog {...restProps}>
        <DialogTitle fontWeight="bold">เพิ่มรายการประเทศ</DialogTitle>
        <DialogContent>กำลังโหลดข้อมูล…</DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog {...restProps}>
        <DialogTitle fontWeight="bold">เพิ่มรายการประเทศ</DialogTitle>
        <DialogContent sx={{ color: 'error.main' }}>
          เกิดข้อผิดพลาดในการโหลดข้อมูลประเทศ
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog {...restProps}>
      <DialogTitle fontWeight="bold">
        {isEdit ? 'แก้ไขรายการประเทศ' : 'เพิ่มรายการประเทศ'}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '' }}>
        <Autocomplete
          value={country}
          onChange={(_, newValue) => setCountry(newValue)}
          options={getCountryOptions()}
          getOptionLabel={(option) => option.label ?? ''}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="กรุณาเลือกประเทศ"
              placeholder="ค้นหา"
              sx={{ width: '100%' }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose}>
          ยกเลิก
        </Button>
        <Button onClick={handleSubmit} disabled={!country}>
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountryDialog;
